<template>
  <v-app>
    <!-- Clearment Header -->
    <header 
      :class="['clearment-header', { 'scrolled': isScrolled }]"
      v-scroll="handleScroll"
    >
      <div class="header-container">
        <!-- Mobile Menu Button -->
        <button 
          v-if="!baseStore.isDesktop && userData" 
          class="mobile-menu-button" 
          @click="showMenue = !showMenue"
        >
          <v-icon size="24">mdi-menu</v-icon>
        </button>

        <!-- Logo -->
        <div class="header-logo">
          <router-link to="/" class="logo-link">
            <img :src="clearmentLogo" alt="Clearment Logo" class="logo-image" />
          </router-link>
        </div>

        <!-- Navigation (Desktop) -->
        <nav v-if="baseStore.isDesktop" class="main-nav"></nav>

        <!-- Right Menu -->
        <div class="header-right">
          <!-- <button v-if="baseStore.isDesktop" class="contact-button" @click="toggleContact">
            <v-icon size="18" class="mr-1">mdi-phone</v-icon>
            <span>{{ t('app.buttons.contact') }}</span>
          </button>
           -->
          <button class="language-button" @click="toggleLanguage">
            <v-icon size="18">mdi-web</v-icon>
            <span class="language-code">{{ currentLanguageLabel }}</span>
          </button>

          <button
            v-if="baseStore.isDesktop && isLoggedIn && !isAdmin"
            class="care-button"
            @click="goToClearmentAI"
          >
            <v-icon size="18" class="mr-1">mdi-brain</v-icon>
            <span>Clearment.AI</span>
          </button>

          <button
            v-if="baseStore.isDesktop && isLoggedIn && !isAdmin"
            class="care-button"
            @click="goToClearmentCare"
          >
            <v-icon size="18" class="mr-1">mdi-shield-account</v-icon>
            <span>ClearmentCare</span>
          </button>

          <button
            v-if="!isLoggedIn"
            class="login-button"
            @click="openLoginDialog"
          >
            <v-icon size="18" class="mr-1">mdi-login</v-icon>
            <span>{{ t('app.buttons.signIn') }}</span>
          </button>
          <button
            v-else
            class="login-button"
            @click="logoutUser"
            :disabled="isProcessingLogout"
          >
            <v-icon size="18" class="mr-1">mdi-logout</v-icon>
            <span>{{ t('app.userMenu.logout') }}</span>
          </button>
        </div>
      </div>
    </header>

    <!-- User Menu Dropdown -->
    <transition name="fade"></transition>

    <!-- Navigation Drawer - Desktop (Persistent) -->
    <v-navigation-drawer 
      v-model="showMenue" 
      permanent
      v-if="userData && baseStore.isDesktop"
      width="280"
      class="main-drawer"
      :style="{ height: 'calc(100vh - 70px)', top: '70px' }"
    >
      <component :is="currentMenuComponent" />
    </v-navigation-drawer>

    <!-- Navigation Drawer - Mobile (Temporary) -->
    <v-navigation-drawer 
      v-model="showMenue" 
      app 
      temporary
      v-if="userData && !baseStore.isDesktop"
      width="280"
      class="main-drawer-mobile"
    >
      <component :is="currentMenuComponent" />
    </v-navigation-drawer>

    <!-- Main Content -->
    <div id="content" :class="{ 'with-drawer': showMenue && userData && baseStore.isDesktop }">
      <LoaderOverlay />
      <ErrorOverlay />
      <SuccessOverlay />
      <div class="content-wrapper">
        <div ref="routerContainer" class="router-container">
          <router-view v-slot="{ Component }">
            <template v-if="Component">
              <AuthWall v-if="route.meta.requiresAuth">
                <component :is="Component" />
              </AuthWall>
              <component v-else :is="Component" />
            </template>
          </router-view>
        </div>
        
        <!-- Right Sidebar - Desktop Only -->
        <component
          v-if="baseStore.isDesktop && userData && isAdmin"
          :is="currentAdminRightSidebar"
        />
        <RightSidebar
          v-else-if="baseStore.isDesktop && userData"
        />
      </div>
    </div>
    
    <!-- Footer - Full Width -->
    <footer class="footer-custom">
      <div class="footer-content-wrapper">
        <div class="text-center footer-content">
          <span class="footer-text">
            &copy; {{ new Date().getFullYear() }} Clearment.ai. {{ t('app.footer.rightsReserved') }}
            <router-link class="footer-link" to="/info/Impressum">{{ t('app.footer.imprint') }}</router-link>
          </span>
        </div>
      </div>
    </footer>
    <v-dialog 
      v-model="userStore.showLogin" 
      :width="baseStore.isMobile ? '95vw' : '666'"
      :fullscreen="baseStore.isMobile"
    >
      <v-card>
        <v-card-text class="login-card-content">
          <LoginView />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup>
import { inject, ref, onMounted,watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter,useRoute } from 'vue-router'
import { useLanguage } from '@/composables/useLanguage'

import MenueDefault from '@/components/sites/modules/MenueDefault.vue';
import MenueAdminMenu from '@/components/sites/modules/MenueAdminMenu.vue';
import LoginView from '@/components/objects/user/LoginView.vue'
import LoaderOverlay from '@/components/modules/LoaderOverlay.vue'
import ErrorOverlay from '@/components/modules/ErrorOverlay.vue'
import SuccessOverlay from '@/components/modules/SuccessOverlay.vue'
import RightSidebar from '@/components/modules/RightSidebar.vue'
import AuthWall from '@/components/modules/AuthWall.vue'
import RightSidebarAdmin from '@/components/modules/RightSidebarAdmin.vue'
import RightSidebarAdminKunde from '@/components/modules/RightSidebarAdminKunde.vue'
import clearmentLogo from '@/assets/clearmentai.png'



// Stores und Services injizieren
const baseCalls = inject('baseCalls')
const userStore = inject('userStore')
const baseStore = inject('baseStore')
const successStore = inject('successStore')
const loaderStore = inject('loaderStore')
const errorStore = inject('errorStore')
const { languageStore, t } = useLanguage()
const router = useRouter()
const route = useRoute()
const currentLanguageLabel = computed(() => {
  const lang = languageStore?.currentLanguage?.value || 'de'
  return lang.toUpperCase()
})
// Reaktive Variablen
const showMenue = ref(false)
const isScrolled = ref(false)
const showUserMenu = ref(false)
const isProcessingLogout = ref(false)

const { userData } = storeToRefs(userStore)
const isLoggedIn = computed(() => !!userData.value)

const isAdmin = computed(() => {
  const u = userData.value
  if (!u || !u.user_admin) return false
  return Number(u.user_admin) === 1
})

const currentMenuComponent = computed(() => {
  if (!userData.value) return null
  return isAdmin.value ? MenueAdminMenu : MenueDefault
})

const currentAdminRightSidebar = computed(() => {
  if (!isAdmin.value) return RightSidebarAdmin
  // Wenn wir auf der Admin-Support-Detailseite sind, spezielle Sidebar nutzen
  if (route.name === 'adminsupport') {
    return RightSidebarAdminKunde
  }
  return RightSidebarAdmin
})

// Login Dialog
function openLoginDialog() {
  if (!userStore.showLogin) {
    userStore.setLoginView(true)
  }
}

async function logoutUser() {
  if (isProcessingLogout.value) {
    return
  }

  isProcessingLogout.value = true
  try {
    await baseCalls.userGet({ action: 'logout', id: 0 })
    if (typeof userStore.logout === 'function') {
      userStore.logout()
    } else {
      userStore.setUser(null)
      userStore.setLoginView(false)
    }
    successStore.setSuccess(
      true,
      t('app.messages.logoutSuccessTitle'),
      t('app.messages.logoutSuccessBody')
    )
  } catch (err) {
    console.error('Logout Fehler:', err)
    errorStore.setError(true, t('app.messages.logoutError'))
  } finally {
    isProcessingLogout.value = false
  }
}

// Header Scroll Logic
function handleScroll() {
  const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop
  isScrolled.value = currentScrollPosition > 50
}

// Header Menu Functions
function toggleLanguage() {
  languageStore?.toggleLanguage?.()
}

function goToClearmentCare() {
  router.push({ name: 'help' })
}

function goToClearmentAI() {
  router.push({ name: 'brain' })
}

// function toggleContact() {
//   // Implement contact modal/page
//   console.log('Toggle contact')
// }

async function getLoggedUser() {
  const data = { action: 'loggeduser',id: 0 }
  try {
    const response = await baseCalls.userGet(data)
    if (response.data.user) {
      userStore.setUser(response.data.user)
    } 
  } catch (err) {
    console.log(err)
  } 
}

async function loginWithToken() {
  // Prüfe ob Token in Route-Params oder Query vorhanden ist
  const token = route.params.token || route.query.token
  
  if (!token) {
    return // Kein Token vorhanden
  }
  
  console.log('Token-Login wird gestartet...', token)
  
  // Ladezustand aktivieren
  loaderStore.setLoader(true, t('app.messages.tokenLoginLoading'))
  
  try {
    const response = await baseCalls.userPost({
      action: 'startlogin',
      token: token
    })
    
    if (response.data.user) {
      console.log('Token-Login erfolgreich:', response.data.user)
      userStore.setUser(response.data.user)
      successStore.setSuccess(
        true,
        t('app.messages.loginSuccessTitle'),
        t('app.messages.loginSuccessBody')
      )
      
      // Weiterleiten zur Startseite
      router.push('/')
    } else {
      console.error('Token-Login fehlgeschlagen')
      // Zeige Fehlermeldung aber bleibe auf der Seite
      errorStore.setError(true, t('app.messages.invalidToken'))
    }
  } catch (err) {
    console.error('Token-Login Fehler:', err)
    errorStore.setError(true, t('app.messages.tokenLoginError'))
  } finally {
    loaderStore.setLoader(false)
  }
}

watch(userData, (newValue) => {
  if (newValue && baseStore.isDesktop) {
    showMenue.value = true;
  } else {
    showMenue.value = false;  // Auf Mobile standardmäßig geschlossen
  }
});

// Watch route changes
watch(
  () => route.path,
  async (newPath, oldPath) => {
    // Close menus on route change
    showUserMenu.value = false;
    
    // Close drawer on mobile when route changes
    if (!baseStore.isDesktop) {
      showMenue.value = false;
    }
    
    // Prüfe ob neue Route ein Token hat
    const token = route.params.token || route.query.token
    if (token && newPath !== oldPath) {
      await loginWithToken()
    }
  }
);

onMounted(async () => {
  // Prüfe zuerst ob Token in URL vorhanden ist
  const token = route.params.token || route.query.token
  
  if (token) {
    // Wenn Token vorhanden, versuche Token-Login
    await loginWithToken()
  } else {
    // Sonst prüfe ob User bereits eingeloggt ist (via Cookie)
    await getLoggedUser()
  }
});

</script>

<style scoped src="@/assets/styles/sfc/App.css"></style>
